'use client';

import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { supabaseConfigured } from './supabaseClient';
import { installApiAuthInterceptor } from './apiAuth';
import { AuthProvider, useAuth } from './AuthContext';
import { LoginView } from './LoginView';
import { NewPasswordView } from './NewPasswordView';
import { SalesPage } from './SalesPage';
import { initialPreAuthView, type PreAuthView } from './pre-auth-route';
import { InstagramInviteView } from './InstagramInviteView';
import { currentInstagramInviteToken } from './instagram-invite-route';

// Attach the bearer to /api requests before any fetch fires. The installer is a
// no-op when Supabase is unconfigured or off-browser, so this is safe at module
// load and runs exactly once.
installApiAuthInterceptor();

function Gate({ children }: { children: ReactNode }) {
  const { session, loading, passwordRecovery } = useAuth();
  // Where a signed-out visitor starts. The URL decides (see `pre-auth-route`),
  // and after that it is a plain toggle — no routing, because there are two
  // screens and the browser back button has nothing useful to restore between
  // them.
  const [preAuth, setPreAuth] = useState<PreAuthView>(initialPreAuthView);

  if (loading) {
    return <div className="od-loading-shell">Carregando…</div>;
  }
  if (!session) {
    if (preAuth.view === 'login') {
      return (
        <LoginView
          initialMode={preAuth.mode}
          onBack={() => setPreAuth({ view: 'sales' })}
        />
      );
    }
    return (
      <SalesPage
        onSignIn={() => setPreAuth({ view: 'login', mode: 'in' })}
        onSignUp={() => setPreAuth({ view: 'login', mode: 'up' })}
      />
    );
  }
  // A session is not, on its own, permission to enter. Opening a "redefinir
  // senha" link produces one — that is how Supabase lets the password be
  // changed at all — and treating it like an ordinary sign-in is what let
  // people land in the app with the password they had just forgotten still in
  // place, locked out again on their next visit. So this branch sits after the
  // session check, not before it.
  if (passwordRecovery) {
    return <NewPasswordView />;
  }
  return <>{children}</>;
}

const noSubscription = () => () => {};
const noInviteOnServer = () => null;

/**
 * The hosted entry gate. When Supabase is configured (NEXT_PUBLIC_SUPABASE_*),
 * a signed-out visitor sees the sales page — the product has to explain itself
 * before it asks for a password — with the login one click away, and the app
 * mounts only once a session exists. When Supabase is not configured (local
 * dev, desktop, self-hosted) this renders the app directly, byte-identical to
 * the upstream behavior.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  // A client's Instagram invite (hub de clientes, item 4) is someone else's
  // link: it opens with no login, and in place of the app even for a visitor
  // who happens to be signed in to their own account.
  // Read through useSyncExternalStore so the static shell (rendered with no
  // window) hydrates as the app and switches right after, instead of a
  // hydration mismatch. The path never changes under a mounted gate.
  const inviteToken = useSyncExternalStore(noSubscription, currentInstagramInviteToken, noInviteOnServer);
  if (inviteToken) {
    return <InstagramInviteView token={inviteToken} search={window.location.search} />;
  }
  if (!supabaseConfigured) {
    return <>{children}</>;
  }
  return (
    <AuthProvider>
      <Gate>{children}</Gate>
    </AuthProvider>
  );
}
